import React from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'antd';
import { MoreOutlined, EllipsisOutlined } from '@ant-design/icons';
import { getMenuPreset } from '../constants/menuPresets';
import './ActionMenu.scss';
import logger from '../../utils/logger';



/**
 * Design System ActionMenu Component
 *
 * A flexible three-dots action menu (kebab/meatball menu) component with support
 * for preset items, custom items, dividers, and full customization.
 *
 * @example
 * // Basic usage with preset keys
 * <ActionMenu
 *   items={['view', 'edit', 'delete']}
 *   record={rowData}
 *   onAction={(key, record) => handleAction(key, record)}
 * />
 *
 * @example
 * // Custom items with dividers
 * <ActionMenu
 *   items={[
 *     { key: 'view', label: 'View Details', icon: <EyeOutlined /> },
 *     { type: 'divider' },
 *     { key: 'delete', label: 'Delete', danger: true },
 *   ]}
 *   onAction={handleAction}
 * />
 *
 * @example
 * // Mixed presets and custom items
 * <ActionMenu
 *   items={[
 *     'view',
 *     'edit',
 *     { type: 'divider' },
 *     { key: 'custom', label: 'Custom Action', icon: <StarOutlined /> },
 *   ]}
 *   disabledKeys={['edit']}
 *   onAction={handleAction}
 * />
 */
const ActionMenu = ({
    items = [],
    orientation = 'vertical',
    record,
    onAction,
    size = 'medium',
    placement = 'bottomRight',
    disabledKeys = [],
    className = '',
    triggerClassName = '',
    triggerAriaLabel = 'Actions',
    disabled = false,
    ...props
}) => {
    /**
     * Resolve an item to its full configuration
     * - String keys are resolved to presets
     * - Objects are passed through with defaults applied
     */
    const resolveItem = (item) => {
        // Handle dividers
        if (item?.type === 'divider') {
            return { type: 'divider' };
        }

        // Handle string preset keys
        if (typeof item === 'string') {
            const preset = getMenuPreset(item);
            if (!preset) {
                logger.warn(`ActionMenu: Unknown preset key "${item}"`);
                return null;
            }
            return { ...preset };
        }

        // Handle object items
        if (typeof item === 'object' && item !== null) {
            return { ...item };
        }

        return null;
    };

    /**
     * Build menu items for Ant Design Dropdown
     */
    const buildMenuItems = () => {
        return items
            .map((item, index) => {
                const resolved = resolveItem(item);
                if (!resolved) return null;

                // Handle dividers
                if (resolved.type === 'divider') {
                    return { type: 'divider', key: `divider-${index}` };
                }

                const isDisabled = disabledKeys.includes(resolved.key) || resolved.disabled;
                const isDanger = resolved.danger;
                const itemColor = resolved.color;

                return {
                    key: resolved.key,
                    disabled: isDisabled,
                    className: `ds-action-menu__item ${isDanger ? 'ds-action-menu__item--danger' : ''}`,
                    label: (
                        <span
                            className="ds-action-menu__item-content"
                            style={itemColor && !isDanger ? { color: itemColor } : undefined}
                            title={isDisabled ? resolved.disabledReason : undefined}
                        >
                            {resolved.icon && (
                                <span className="ds-action-menu__item-icon">
                                    {resolved.icon}
                                </span>
                            )}
                            <span className="ds-action-menu__item-label">
                                {resolved.label}
                            </span>
                            {resolved.rightIcon && (
                                <span className="ds-action-menu__item-right-icon">
                                    {resolved.rightIcon}
                                </span>
                            )}
                        </span>
                    ),
                    onClick: (menuInfo) => {
                        menuInfo?.domEvent?.stopPropagation();

                        if (isDisabled) return;

                        // Call item-specific onClick if provided
                        if (resolved.onClick) {
                            resolved.onClick(record);
                        }

                        // Call global onAction handler
                        if (onAction) {
                            onAction(resolved.key, record);
                        }
                    },
                };
            })
            .filter(Boolean);
    };

    // Get trigger icon based on orientation
    const TriggerIcon = orientation === 'horizontal' ? EllipsisOutlined : MoreOutlined;

    // Size classes
    const sizeClassMap = {
        small: 'ds-action-menu__trigger--small',
        medium: 'ds-action-menu__trigger--medium',
        large: 'ds-action-menu__trigger--large',
    };

    const triggerClasses = [
        'ds-action-menu__trigger',
        sizeClassMap[size] || sizeClassMap.medium,
        disabled ? 'ds-action-menu__trigger--disabled' : '',
        triggerClassName,
    ].filter(Boolean).join(' ');

    const containerClasses = [
        'ds-action-menu',
        className,
    ].filter(Boolean).join(' ');

    const menuItems = buildMenuItems();

    // Don't render if no valid items
    if (menuItems.length === 0) {
        return null;
    }

    return (
        <div className={containerClasses} {...props}>
            <Dropdown
                menu={{ items: menuItems }}
                trigger={['click']}
                placement={placement}
                classNames={{ root: 'ds-action-menu-dropdown' }}
                disabled={disabled}
            >
                <button
                    type="button"
                    aria-label={triggerAriaLabel}
                    className={triggerClasses}
                    onClick={(e) => e.stopPropagation()}
                    disabled={disabled}
                >
                    <TriggerIcon className="ds-action-menu__icon" />
                </button>
            </Dropdown>
        </div>
    );
};

ActionMenu.propTypes = {
    /**
     * Array of menu items. Can be:
     * - String preset keys: 'view', 'edit', 'delete', etc.
     * - Custom item objects: { key, label, icon, rightIcon, onClick, danger, disabled, disabledReason, color }
     * - Dividers: { type: 'divider' }
     */
    items: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                key: PropTypes.string,
                label: PropTypes.node,
                icon: PropTypes.node,
                rightIcon: PropTypes.node,
                onClick: PropTypes.func,
                danger: PropTypes.bool,
                disabled: PropTypes.bool,
                disabledReason: PropTypes.string,
                color: PropTypes.string,
                type: PropTypes.oneOf(['divider']),
            }),
        ])
    ),

    /** Trigger icon orientation: 'vertical' (⋮) or 'horizontal' (...) */
    orientation: PropTypes.oneOf(['vertical', 'horizontal']),

    /** Data to pass to onClick/onAction handlers */
    record: PropTypes.any,

    /** Global action handler: (key, record) => void */
    onAction: PropTypes.func,

    /** Trigger button size */
    size: PropTypes.oneOf(['small', 'medium', 'large']),

    /** Dropdown placement */
    placement: PropTypes.oneOf(['bottomLeft', 'bottomRight', 'topLeft', 'topRight']),

    /** Array of item keys to disable */
    disabledKeys: PropTypes.arrayOf(PropTypes.string),

    /** Additional CSS class for container */
    className: PropTypes.string,

    /** Additional CSS class for trigger button */
    triggerClassName: PropTypes.string,

    /** Accessible label for the trigger button */
    triggerAriaLabel: PropTypes.string,

    /** Disable the entire menu */
    disabled: PropTypes.bool,
};

ActionMenu.defaultProps = {
    items: [],
    orientation: 'vertical',
    record: undefined,
    onAction: undefined,
    size: 'medium',
    placement: 'bottomRight',
    disabledKeys: [],
    className: '',
    triggerClassName: '',
    triggerAriaLabel: 'Actions',
    disabled: false,
};

export default ActionMenu;
